import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiClient from 'api/apiClients';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import SvgUser from 'components/svg/SvgUser';
import { useClients } from '../context/ClientsProvider';
import useDeleteClient from '../hooks/useDeleteClient';
import SolarIcon from 'components/icons/SolarIcon';
import { useAuth } from 'context/AuthContext';
import { canViewSales, hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import type { NewSaleType } from 'modules/objects';
import type { NewObjectType } from 'modules/lead';

interface ClientActionProps {
    client: any;
    fetchClients: (force?: boolean) => Promise<void>;
    setEditClientId: (clientId: number) => void;
    setInfoClientId: (clientId: number) => void;
    setSubjectsClientId: (clientId: number) => void;
    onCreateSale?: (sale: NewSaleType) => void;
    onCreateObject?: (object: NewObjectType) => void;
}

const ClientAction = ({ client, fetchClients, setEditClientId, setInfoClientId, setSubjectsClientId, onCreateSale, onCreateObject }: ClientActionProps) => {
    const { t } = useTranslation(['Clients', 'Objects', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const { setShowSpinner } = useClients();
    const { deleteClient } = useDeleteClient();
    const canCreateSale = canViewSales(auth?.user);
    const canCreateObject = hasUserAccess(auth?.user, 'objectCreate');

    const cbDeleteClient = async () => {
        setShowSpinner(true);
        const resDelete = await apiClient.deleteClient(client.ID);
        await fetchClients(true);
        setShowSpinner(false);

        if (resDelete) showToast(t('messClientDeleted'), 'success');
        else showToast(t('common:messError'), 'danger');
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${options?.danger ? ' text-danger' : ''}`}>
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('clientPopup.edit'),
                t('clientPopup.editHint'),
                () => setEditClientId(client.ID),
            )}

            {renderMenuItem(
                <SvgUser />,
                t('clientPopup.view'),
                t('clientPopup.viewHint'),
                () => setInfoClientId(client.ID),
            )}

            {renderMenuItem(
                <SolarIcon name="person-vcard" />,
                t('clientPopup.subjects'),
                t('clientPopup.subjectsHint'),
                () => setSubjectsClientId(client.ID),
            )}

            <Dropdown.Divider className="my-2" />

            {canCreateSale && onCreateSale ? (
                renderMenuItem(
                    <SolarIcon name="wallet2" />,
                    t('Objects:toolAddSale'),
                    t('clientPopup.saleHint'),
                    () =>
                        onCreateSale({
                            clientId: client.ID,
                            clientName: client.clientName,
                            clientPhone: client.clientPhone,
                            clientEmail: client.clientEmail,
                            objectName: client.clientName || '',
                        }),
                )
            ) : null}

            {canCreateObject && onCreateObject ? (
                renderMenuItem(
                    <SolarIcon name="brightness-high" />,
                    t('Objects:toolAddObject'),
                    t('clientPopup.objectHint'),
                    () =>
                        onCreateObject({
                            clientId: client.ID,
                            clientName: client.clientName,
                            clientPhone: client.clientPhone,
                            clientEmail: client.clientEmail,
                            objectName: client.clientName || '',
                        }),
                )
            ) : null}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('clientPopup.delete'),
                t('clientPopup.deleteHint'),
                () => deleteClient(client, cbDeleteClient),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle className="p-1 py-0" size="sm" variant="light-secondary" id={`dropdown-client-${client.ID}`}>
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ClientAction;
